/**
 * Distribute Rewards Dialog Component
 * Allows admins to distribute rewards to users
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Checkbox,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Chip,
} from '@mui/material'
import { Close, Search, Send, Person, CheckCircle } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import { searchApi } from '@api/endpoints/search'
import { useDistributeRewards } from '../hooks/useRewardTemplates'
import type { GiftRewardTemplateDto } from '@api/types/rewards'
import { RewardSourceType } from '@api/types/rewards'

interface DistributeRewardsDialogProps {
  open: boolean
  onClose: () => void
  template: GiftRewardTemplateDto | null
}

interface SelectedUser {
  userId: string
  displayName: string
  avatarUrl?: string
}

export function DistributeRewardsDialog({
  open,
  onClose,
  template,
}: DistributeRewardsDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const distributeMutation = useDistributeRewards()

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch group members for initial display (when no search)
  const { data: members, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['group-members', template?.groupId],
    queryFn: () => socialApi.getGroupMembers(template!.groupId!),
    enabled: open && !!template?.groupId && !debouncedQuery,
  })

  // Search for users when query is entered
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['user-search', debouncedQuery],
    queryFn: () => searchApi.quickSearch(debouncedQuery, 'users', 20),
    enabled: open && debouncedQuery.length >= 2,
  })

  const handleClose = () => {
    setSearchQuery('')
    setDebouncedQuery('')
    setSelectedUsers([])
    setError(null)
    setSuccess(false)
    onClose()
  }

  const handleToggleUser = (user: SelectedUser) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.userId === user.userId)
        ? prev.filter((u) => u.userId !== user.userId)
        : [...prev, user]
    )
  }

  const handleSelectAll = () => {
    if (!members) return
    const allUsers: SelectedUser[] = members.map((m: any) => ({
      userId: m.userId,
      displayName: m.displayName,
      avatarUrl: m.avatarUrl,
    }))
    setSelectedUsers(allUsers)
  }

  const handleDeselectAll = () => {
    setSelectedUsers([])
  }

  const handleDistribute = async () => {
    if (!template || !template.groupId || selectedUsers.length === 0) return

    setError(null)
    try {
      const result = await distributeMutation.mutateAsync({
        groupId: template.groupId,
        templateId: template.id,
        data: {
          userIds: selectedUsers.map((u) => u.userId),
          sourceType: RewardSourceType.Group,
          sourceId: template.groupId,
        },
      })

      if (result.success) {
        setSuccess(true)
        // Clear selection after success
        setSelectedUsers([])
      } else {
        setError(result.message || 'Failed to distribute rewards')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to distribute rewards')
    }
  }

  // Determine which users to display
  const isSearchMode = debouncedQuery.length >= 2
  const isLoading = isSearchMode ? isSearching : isLoadingMembers

  // Handle search results - API may return array directly or object with users property
  const searchResultsArray = Array.isArray(searchResults)
    ? searchResults
    : (searchResults as any)?.users || []

  const displayUsers = isSearchMode
    ? searchResultsArray.map((r: any) => ({
        userId: r.id || r.userId,
        displayName: r.displayName,
        avatarUrl: r.avatarUrl || r.avatar || r.profilePicture,
        userName: r.userName,
      }))
    : members || []

  if (!template) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Distribute Rewards</Typography>
        <Button onClick={handleClose} color="inherit" size="small">
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent>
        {/* Template info */}
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Template
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {template.title}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            Rewards distributed successfully!
          </Alert>
        )}

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          helperText={isSearchMode ? 'Searching all users' : 'Type to search for users'}
        />

        {/* Selected users display */}
        {selectedUsers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Selected ({selectedUsers.length})
              </Typography>
              <Button size="small" onClick={handleDeselectAll}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedUsers.map((user) => (
                <Chip
                  key={user.userId}
                  label={user.displayName}
                  size="small"
                  onDelete={() => handleToggleUser(user)}
                  avatar={<Avatar src={user.avatarUrl}><Person /></Avatar>}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Selection controls for group members (when not searching) */}
        {!isSearchMode && members && members.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Group Members
            </Typography>
            <Button size="small" onClick={handleSelectAll}>
              Select All
            </Button>
          </Box>
        )}

        {/* Users list */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : displayUsers && displayUsers.length > 0 ? (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {displayUsers.map((user: any) => {
              const userId = user.userId || user.id
              const isSelected = selectedUsers.some((u) => u.userId === userId)
              return (
                <ListItem
                  key={userId}
                  dense
                  button
                  onClick={() => handleToggleUser({
                    userId,
                    displayName: user.displayName,
                    avatarUrl: user.avatarUrl,
                  })}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl}>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName}
                    secondary={user.userName ? `@${user.userName}` : user.roleName}
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge="end"
                      checked={isSelected}
                      onChange={() => handleToggleUser({
                        userId,
                        displayName: user.displayName,
                        avatarUrl: user.avatarUrl,
                      })}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              )
            })}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {isSearchMode ? 'No users found' : 'No group members'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={distributeMutation.isPending ? <CircularProgress size={20} /> : <Send />}
          onClick={handleDistribute}
          disabled={selectedUsers.length === 0 || distributeMutation.isPending}
        >
          {distributeMutation.isPending
            ? 'Distributing...'
            : `Distribute to ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
